import { createContext, useState, useEffect, useContext, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { sessionTimeout, SessionTimeoutCallbacks } from "@/lib/sessionTimeout";
import { SessionTimeoutWarning } from "@/components/SessionTimeoutWarning";
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/queryClient';

export type SubscriptionStatus = {
  subscribed: boolean;
  subscription_tier: string | null; // e.g., "Free", "Pro", "Enterprise"
  subscription_end: string | null; // ISO date string
  form_limit: number | null;
  backup_limit: number | null;
  stripe_customer_id?: string | null;
  active_price_id?: string | null; // Stripe Price ID of the current active plan
  active_price_id_key?: string | null; // e.g. "pro_monthly", "enterprise_annual"
  billing_status?: string | null;
  billing_access_active?: boolean;
  billing_has_history?: boolean;
  cancel_at_period_end?: boolean | null;
};

type AuthContextType = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  subscription: SubscriptionStatus | null;
  isSubscriptionLoading: boolean;
  signUp: (email: string, password: string, acceptedTerms?: boolean) => Promise<{ session: Session | null; user: User | null }>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  fetchSubscriptionStatus: (currentUser?: User | null) => Promise<void>; // Allow passing user
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultFreeSubscription: SubscriptionStatus = {
  subscribed: false,
  subscription_tier: "Free",
  subscription_end: null,
  form_limit: 1,
  backup_limit: 50,
  active_price_id: null,
  active_price_id_key: "free",
  billing_status: "free",
  billing_access_active: true,
  billing_has_history: false,
  cancel_at_period_end: false,
};

// Helper to create a timeout promise
// Added a trailing comma after <T,> to disambiguate from JSX in .tsx files
const withTimeout = <T,>(promise: Promise<T>, ms: number, timeoutError = new Error('Promise timed out')): Promise<T> => {
  const timeout = new Promise<T>((_, reject) => {
    setTimeout(() => {
      reject(timeoutError);
    }, ms);
  });
  return Promise.race([promise, timeout]);
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  
  // Session timeout state
  const [sessionWarningVisible, setSessionWarningVisible] = useState(false);
  const [sessionRemainingTime, setSessionRemainingTime] = useState(0);

  const fetchSubscriptionStatus = async (currentUser?: User | null) => {
    const userForFetch = currentUser !== undefined ? currentUser : user;

    if (!userForFetch) {
      logger.debug("AuthContext: fetchSubscriptionStatus - No user, setting default free subscription.");
      setSubscription(defaultFreeSubscription);
      setIsSubscriptionLoading(false);
      return;
    }

    logger.debug("AuthContext: fetchSubscriptionStatus - Setting isSubscriptionLoading to true for user:", userForFetch.id);
    setIsSubscriptionLoading(true);
    logger.debug("AuthContext: fetchSubscriptionStatus - Attempting to invoke 'check-subscription' function with a 10s timeout.");
    try {
      const subscriptionPromise = supabase.functions.invoke("check-subscription");
      const { data, error } = await withTimeout(subscriptionPromise, 10000); // 10 second timeout

      logger.debug("AuthContext: fetchSubscriptionStatus - 'check-subscription' invoked. Data:", data, "Error:", error);
      if (error) {
        logger.error("AuthContext: fetchSubscriptionStatus - Error from 'check-subscription':", error);
        throw error; // This will be caught by the outer catch
      }
      logger.debug("AuthContext: fetchSubscriptionStatus - Subscription data received:", data);
      logger.debug("🔍 DEBUG: subscription_tier value:", data?.subscription_tier);
      logger.debug("🔍 DEBUG: subscription_tier is null?", data?.subscription_tier === null);
      logger.debug("🔍 DEBUG: subscription_tier is undefined?", data?.subscription_tier === undefined);
      logger.debug("🔍 DEBUG: full subscription object:", JSON.stringify(data, null, 2));
      setSubscription(data as SubscriptionStatus);
    } catch (error: unknown) {
      logger.error("AuthContext: fetchSubscriptionStatus - Catch block. Failed to fetch subscription status (or timed out):", error);
      toast({
        variant: "destructive",
        title: "Subscription Check Failed",
        description: error instanceof Error ? error.message : "Could not retrieve your subscription details. Defaulting to Free plan.",
      });
      setSubscription(defaultFreeSubscription); // Fallback to free on error or timeout
    } finally {
      logger.debug("AuthContext: fetchSubscriptionStatus - Finally block. Setting isSubscriptionLoading to false.");
      setIsSubscriptionLoading(false);
      logger.debug("AuthContext: fetchSubscriptionStatus - Finished fetching subscription status.");
    }
  };

  useEffect(() => {
    logger.debug("AuthContext: useEffect - Initializing auth state.");
    setIsLoading(true);

    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      logger.debug("AuthContext: useEffect - Initial getSession result", currentSession);
      setSession(currentSession);
      const currentUser = currentSession?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        logger.debug("AuthContext: useEffect - Initial session has user, fetching subscription.");
        await fetchSubscriptionStatus(currentUser);
      } else {
        logger.debug("AuthContext: useEffect - Initial session no user, setting default free subscription.");
        setSubscription(defaultFreeSubscription);
        setIsSubscriptionLoading(false); // Explicitly set here
      }
      logger.debug("AuthContext: useEffect - Initial auth loading finished.");
      setIsLoading(false);
    }).catch(error => {
      logger.error("AuthContext: useEffect - Error in getSession():", error);
      setSubscription(defaultFreeSubscription);
      setIsSubscriptionLoading(false);
      setIsLoading(false);
    });

    // Helper function to record TOS acceptance after email confirmation
    const recordTosAcceptanceIfNeeded = async (authUser: User) => {
      // Check if user accepted TOS during signup (stored in metadata) but it hasn't been recorded yet
      const tosVersion = authUser.user_metadata?.accepted_customer_tos_version;
      const tosRecorded = authUser.user_metadata?.tos_recorded_in_audit;

      if (tosVersion && !tosRecorded) {
        logger.debug("AuthContext: Recording TOS acceptance for user after email confirmation", authUser.id);
        try {
          const { error: tosError } = await supabase.functions.invoke("record-tos-acceptance", {
            body: {
              tos_version: tosVersion,
              tos_type: "customer_tos",
              metadata: {
                signup_flow: true,
                recorded_after_email_confirmation: true,
                original_acceptance_time: authUser.user_metadata?.accepted_customer_tos_at,
              }
            }
          });

          if (tosError) {
            logger.error("AuthContext: Failed to record TOS in audit table:", tosError);
          } else {
            logger.debug("AuthContext: TOS acceptance recorded successfully, updating user metadata");
            // Mark TOS as recorded so we don't try again
            await supabase.auth.updateUser({
              data: { tos_recorded_in_audit: true }
            });
          }
        } catch (error) {
          logger.error("AuthContext: Unexpected error recording TOS:", error);
        }
      }
    };

    const { data: { subscription: authListener } } = supabase.auth.onAuthStateChange(
      (event, newSession) => { // Removed async here
        logger.debug("AuthContext: onAuthStateChange event:", event, "session:", newSession);

        setSession(newSession);
        const newAuthUser = newSession?.user ?? null;
        setUser(newAuthUser);

        // Fallback for /welcome routing: if this SIGNED_IN is from a very
        // recent email confirmation and the signup hash was consumed before
        // Auth.tsx could read it, seed the marker so the post-auth redirect
        // still routes through /welcome. Primary path is the hash sniffer in
        // Auth.tsx.
        if (event === "SIGNED_IN" && newAuthUser?.email_confirmed_at) {
          const confirmedAgoMs = Date.now() - new Date(newAuthUser.email_confirmed_at).getTime();
          if (confirmedAgoMs >= 0 && confirmedAgoMs < 60_000) {
            try {
              sessionStorage.setItem("fab_signup_confirming", "1");
            } catch {
              // sessionStorage unavailable — hash path still covers the common case.
            }
          }
        }

        if (
          event === "SIGNED_IN"
          || event === "TOKEN_REFRESHED"
          || event === "USER_UPDATED"
          || event === "MFA_CHALLENGE_VERIFIED"
        ) {
          if (newAuthUser) {
            logger.debug("AuthContext: onAuthStateChange - Auth state updated, queueing subscription fetch for", newAuthUser.id, "event:", event);
            // Record TOS if needed (after email confirmation)
            recordTosAcceptanceIfNeeded(newAuthUser);
            setTimeout(() => {
              fetchSubscriptionStatus(newAuthUser);
            }, 0);
          } else {
            logger.debug("AuthContext: onAuthStateChange - SIGNED_IN event but no user in session, setting free sub.");
            setSubscription(defaultFreeSubscription);
            setIsSubscriptionLoading(false);
          }
        } else if (event === "SIGNED_OUT") {
          logger.debug("AuthContext: onAuthStateChange - User signed out, setting subscription to defaultFreeSubscription.");
          setSubscription(defaultFreeSubscription); 
          setIsSubscriptionLoading(false);
        } else if (event === "INITIAL_SESSION") {
            logger.debug("AuthContext: onAuthStateChange - INITIAL_SESSION event triggered.");
            if (newAuthUser && (!subscription || isSubscriptionLoading)) { 
                logger.debug("AuthContext: onAuthStateChange - INITIAL_SESSION with user, queueing subscription fetch if not already loaded/loading.");
                 setTimeout(() => { 
                    fetchSubscriptionStatus(newAuthUser);
                 },0);
            } else if (!newAuthUser) {
                logger.debug("AuthContext: onAuthStateChange - INITIAL_SESSION no user, setting free sub.");
                setSubscription(defaultFreeSubscription);
                setIsSubscriptionLoading(false);
            } else {
                logger.debug("AuthContext: onAuthStateChange - INITIAL_SESSION with user, but subscription might already be set or not loading. Current sub:", subscription, "isLoading:", isSubscriptionLoading);
            }
        } else {
          logger.debug("AuthContext: onAuthStateChange - Unhandled event type or state:", event);
        }
      }
    );

    return () => {
      logger.debug("AuthContext: useEffect - Cleaning up auth listener.");
      authListener.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const signUp = async (email: string, password: string, acceptedTerms: boolean = false) => {
    try {
      // Prepare user metadata with ToS acceptance info (convenience flag, not authoritative)
      const metadata = acceptedTerms ? {
        accepted_customer_tos_version: "2025-10-24",
        accepted_customer_tos_at: new Date().toISOString(),
      } : {};

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      if (error) throw error;

      // If terms were accepted AND we have a session (no email confirmation required),
      // record it immediately in the audit table via edge function.
      // If email confirmation IS required, the onAuthStateChange handler will record it
      // when the user confirms their email and gets a session.
      if (acceptedTerms && data.user && data.session) {
        try {
          logger.debug("AuthContext: signUp - Recording ToS acceptance in audit table (immediate, no email confirmation)");
          const { error: tosError } = await supabase.functions.invoke("record-tos-acceptance", {
            body: {
              tos_version: "2025-10-24",
              tos_type: "customer_tos",
              metadata: {
                signup_flow: true,
                account_type: "free", // Will be updated when user selects tier
              }
            }
          });

          if (tosError) {
            // Log error but don't fail signup - the metadata is still recorded
            logger.error("AuthContext: signUp - Failed to record ToS in audit table:", tosError);
          } else {
            logger.debug("AuthContext: signUp - ToS acceptance recorded successfully, marking in metadata");
            // Mark as recorded so onAuthStateChange doesn't try again
            await supabase.auth.updateUser({
              data: { tos_recorded_in_audit: true }
            });
          }
        } catch (tosRecordError) {
          logger.error("AuthContext: signUp - Unexpected error recording ToS:", tosRecordError);
          // Don't fail the signup if ToS recording fails
        }
      }

      // Return the result so caller can check if email confirmation is required
      const needsConfirmation = !data.session || !data.user;

      if (needsConfirmation) {
        toast({
          title: "Account created!",
          description: "Please check your email to confirm your account.",
        });
        logger.debug("AuthContext: signUp - User not immediately logged in (email confirmation required).");
        setSubscription(defaultFreeSubscription);
        setIsSubscriptionLoading(false);
      } else {
        toast({
          title: "Account created!",
          description: "Welcome! Your account is ready.",
        });
        logger.debug("AuthContext: signUp - User logged in after signup, queueing subscription fetch for new user:", data.user.id);
        setTimeout(() => {
          fetchSubscriptionStatus(data.user);
        }, 0);
      }

      return { session: data.session, user: data.user };
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Sign up failed",
        description: error instanceof Error ? error.message : "Sign up failed",
      });
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      logger.debug("AuthContext: signIn - Successful. onAuthStateChange will handle subscription fetch for user:", data?.user?.id);
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in.",
      });
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Sign in failed",
        description: error instanceof Error ? error.message : "Sign in failed",
      });
      throw error;
    }
  };

  // Track the warning update interval
  const [warningIntervalRef] = useState<{ current: NodeJS.Timeout | null }>({ current: null });

  // Start session timeout when user signs in
  useEffect(() => {
    if (user && session) {
      logger.debug("AuthContext: Starting session timeout monitoring for user:", user.id);

      // Create callbacks inside useEffect to avoid dependency issues
      const sessionTimeoutCallbacks: SessionTimeoutCallbacks = {
        onWarning: (remainingMs: number) => {
          setSessionRemainingTime(remainingMs);
          setSessionWarningVisible(true);

          // Clear any existing interval
          if (warningIntervalRef.current) {
            clearInterval(warningIntervalRef.current);
          }

          // Update remaining time every second while warning is visible
          warningIntervalRef.current = setInterval(() => {
            const newRemainingTime = sessionTimeout.getWarningTime();
            if (newRemainingTime > 0) {
              setSessionRemainingTime(newRemainingTime);
            } else {
              if (warningIntervalRef.current) {
                clearInterval(warningIntervalRef.current);
                warningIntervalRef.current = null;
              }
            }
          }, 1000);
        },
        onTimeout: () => {
          // Clear warning interval on timeout
          if (warningIntervalRef.current) {
            clearInterval(warningIntervalRef.current);
            warningIntervalRef.current = null;
          }
          setSessionWarningVisible(false);
          toast({
            title: "Session Expired",
            description: "You have been logged out due to inactivity.",
            variant: "default",
            duration: Infinity,
          });
          signOut(true); // Skip the "Signed out" toast since we already showed "Session Expired"
        },
        onActivityDetected: () => {
          // Clear warning interval when activity detected
          if (warningIntervalRef.current) {
            clearInterval(warningIntervalRef.current);
            warningIntervalRef.current = null;
          }
          setSessionWarningVisible(false);
        },
      };

      sessionTimeout.start(sessionTimeoutCallbacks);
    } else {
      logger.debug("AuthContext: Stopping session timeout monitoring");
      sessionTimeout.stop();
      setSessionWarningVisible(false);
      // Clear warning interval on cleanup
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
    }

    return () => {
      sessionTimeout.stop();
      // Clear warning interval on cleanup
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
    };
  }, [user, session, warningIntervalRef]);

  const extendSession = () => {
    sessionTimeout.extendSession();
    setSessionWarningVisible(false);
    toast({
      title: "Session Extended",
      description: "Your session has been extended.",
    });
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      
      toast({
        title: "Password reset sent",
        description: "Check your email for a link to reset your password.",
      });
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Password reset failed",
        description: error instanceof Error ? error.message : "Password reset failed",
      });
      throw error;
    }
  };

  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      
      toast({
        title: "Password updated",
        description: "Your password has been updated successfully.",
      });
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Password update failed",
        description: error instanceof Error ? error.message : "Password update failed",
      });
      throw error;
    }
  };

  const signOut = async (skipToast: boolean = false) => {
    logger.debug("AuthContext: signOut - Attempting sign out. Immediately resetting local state.", { skipToast });

    // Stop session timeout monitoring
    sessionTimeout.stop();
    setSessionWarningVisible(false);

    // Cancel all in-flight queries and clear cache to prevent post-logout API calls
    logger.debug("AuthContext: signOut - Cancelling all queries and clearing cache.");
    queryClient.cancelQueries();
    queryClient.clear();

    setUser(null);
    setSession(null);
    setSubscription(defaultFreeSubscription);
    setIsSubscriptionLoading(false);

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        logger.error("AuthContext: signOut - Error during Supabase signOut():", error);
        if (!skipToast) {
          toast({
            variant: "default",
            title: "Sign Out Incomplete",
            description: "Could not fully sign out from the server. Please try again later.",
          });
        }
      } else {
        logger.debug("AuthContext: signOut - Supabase signOut() successful.");
        // Only show success toast if not from timeout (timeout already shows its own toast)
        if (!skipToast) {
          toast({
            title: "Signed out",
            description: "You have been signed out successfully",
          });
        }
      }
    } catch (error: unknown) {
      logger.error("AuthContext: signOut - Unexpected error during signOut process:", error);
      if (!skipToast) {
        toast({
          variant: "destructive",
          title: "Sign out failed",
          description: error instanceof Error ? error.message : "An unexpected error occurred during sign out.",
        });
      }
    }
  };

  const value = {
    session,
    user,
    isLoading,
    subscription,
    isSubscriptionLoading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    fetchSubscriptionStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutWarning
        remainingTimeMs={sessionRemainingTime}
        onExtendSession={extendSession}
        onLogout={signOut}
        isVisible={sessionWarningVisible}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
